import { Box, List } from "@mui/material";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import type { ReactNode } from "react";
import { use, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FieldTreeScrollContext } from "./FieldTreeScrollContext.ts";

interface Props<T> {
  fields: T[];
  groupCaption: string;
  visible: boolean;
  renderField: (field: T, groupCaption: string) => ReactNode;
}

const VirtualizedFieldList = <T extends { name: string }>({ fields, groupCaption, visible, renderField }: Props<T>) => {
  const { scrollElement, contentElement } = use(FieldTreeScrollContext);
  const [pinnedRowId, setPinnedRowId] = useState<string>();
  const [scrollMargin, setScrollMargin] = useState<number>();
  const [inViewport, setInViewport] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const pinnedIndex = useMemo(() => fields.findIndex((field) => field.name === pinnedRowId), [fields, pinnedRowId]);
  const getItemKey = useCallback((index: number) => fields[index]?.name ?? index, [fields]);
  const rangeExtractor = useCallback(
    (range: Parameters<typeof defaultRangeExtractor>[0]) => {
      const indexes = inViewport ? defaultRangeExtractor(range) : [];
      return pinnedIndex < 0 || indexes.includes(pinnedIndex)
        ? indexes
        : [...indexes, pinnedIndex].sort((a, b) => a - b);
    },
    [pinnedIndex, inViewport]
  );
  const virtualizer = useVirtualizer({
    count: fields.length,
    getScrollElement: () => scrollElement,
    initialOffset: () => scrollElement?.scrollTop ?? 0,
    estimateSize: () => 28,
    getItemKey,
    scrollMargin: scrollMargin ?? 0,
    overscan: 5,
    rangeExtractor,
    enabled: visible && scrollMargin !== undefined,
  });

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!visible || !list || !scrollElement || !contentElement) {
      return;
    }
    const measureOffset = () =>
      setScrollMargin(
        list.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop
      );
    const observer = new ResizeObserver(measureOffset);
    observer.observe(contentElement);
    observer.observe(scrollElement);
    const intersectionObserver = new IntersectionObserver(([entry]) => setInViewport(entry?.isIntersecting === true), {
      root: scrollElement,
      rootMargin: "140px 0px",
    });
    intersectionObserver.observe(list);
    return () => {
      observer.disconnect();
      intersectionObserver.disconnect();
    };
  }, [scrollElement, contentElement, visible]);

  return (
    <List
      ref={listRef}
      sx={{
        p: 0,
        position: "relative",
        height: visible && scrollMargin === undefined ? fields.length * 28 : virtualizer.getTotalSize(),
      }}
    >
      {virtualizer.getVirtualItems().map((item) => {
        const field = fields[item.index];
        if (!field) {
          return null;
        }
        return (
          <Box
            key={item.key}
            data-index={item.index}
            ref={virtualizer.measureElement}
            onFocusCapture={() => setPinnedRowId(field.name)}
            onDragStartCapture={() => setPinnedRowId(field.name)}
            sx={{
              position: "absolute",
              top: 0,
              width: "100%",
              transform: `translateY(${item.start - (scrollMargin ?? 0)}px)`,
            }}
          >
            {renderField(field, groupCaption)}
          </Box>
        );
      })}
    </List>
  );
};

export default VirtualizedFieldList;
